import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Workflow,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Power,
  PowerOff,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useWorkflowDetailQuery,
  useWorkflowExecutionsQuery,
  useWorkflowExecutionQuery,
  useWorkflowStatsQuery,
} from '@/client/api/workflows/workflows.query';
import {
  useActivateWorkflowMutation,
  useDeactivateWorkflowMutation,
} from '@/client/api/workflows/workflows.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  WorkflowAccessDenied,
  formatDateTime,
  formatDuration,
  isN8nUnconfigured,
  statusBadgeClass,
} from '@/client/components/admin/Workflows/workflowUi';

type Tab = 'overview' | 'executions' | 'nodes';
type ExecFilter = '' | 'success' | 'error' | 'waiting';

const WINDOWS = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
] as const;

const WorkflowDetailPage = () => {
  const { wid = '' } = useParams<{ wid: string }>();

  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows', href: '/admin/workflows' },
    { label: wid || 'Detail' },
  ]);

  const viewPerm = useHasPermission('workflow.view');
  const managePerm = useHasPermission('workflow.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [tab, setTab] = useState<Tab>('overview');
  const [hours, setHours] = useState(24);
  const [execStatus, setExecStatus] = useState<ExecFilter>('');
  const [selectedExecId, setSelectedExecId] = useState<string | null>(null);

  const detailQ = useWorkflowDetailQuery(wid, canView);
  const statsQ = useWorkflowStatsQuery(wid, hours, canView && tab === 'overview');
  const execsQ = useWorkflowExecutionsQuery(
    wid,
    { status: execStatus || undefined, limit: 50 },
    canView && tab === 'executions',
  );
  const execDetailQ = useWorkflowExecutionQuery(
    wid,
    selectedExecId,
    canView && !!selectedExecId,
  );

  const activate = useActivateWorkflowMutation();
  const deactivate = useDeactivateWorkflowMutation();

  const workflow = detailQ.data?.workflow;
  const toggling = activate.isPending || deactivate.isPending;

  const failingNodes = useMemo(() => {
    const map = statsQ.data?.errors_by_failing_node ?? {};
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [statsQ.data?.errors_by_failing_node]);

  const handleToggle = async () => {
    if (!workflow) return;
    try {
      if (workflow.active) {
        await deactivate.mutateAsync(wid);
        toast.success('Workflow deactivated', { richColors: true });
      } else {
        await activate.mutateAsync(wid);
        toast.success('Workflow activated', { richColors: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Toggle failed'), { richColors: true });
    }
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <WorkflowAccessDenied />
        </div>
      </div>
    );
  }

  const n8nDown = detailQ.isError && isN8nUnconfigured(detailQ.error);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <Link
              to="/admin/workflows"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" /> All workflows
            </Link>
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
                <Workflow className="h-5 w-5" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold text-gray-900">
                    {workflow?.name ?? wid}
                  </h1>
                  {workflow && (
                    <Badge
                      variant="outline"
                      className={
                        workflow.active
                          ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }
                    >
                      {workflow.active ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 font-mono text-xs text-gray-400">{wid}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                detailQ.refetch();
                statsQ.refetch();
                execsQ.refetch();
              }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {canManage && workflow && (
              <Button
                size="sm"
                variant={workflow.active ? 'outline' : 'default'}
                disabled={toggling}
                onClick={handleToggle}
                className="gap-2"
              >
                {toggling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : workflow.active ? (
                  <PowerOff className="h-4 w-4" />
                ) : (
                  <Power className="h-4 w-4" />
                )}
                {workflow.active ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </div>
        </div>

        {n8nDown && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              n8n is not configured (<code className="rounded bg-amber-100 px-1">n8n_unconfigured</code>
              ).
            </p>
          </div>
        )}

        {detailQ.isError && !n8nDown && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {getApiErrorMessage(detailQ.error, 'Failed to load workflow')}
          </div>
        )}

        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-0.5 text-sm w-fit">
          {(
            [
              ['overview', 'Overview'],
              ['executions', 'Executions'],
              ['nodes', 'Nodes'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`rounded-md px-3 py-1.5 ${
                tab === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Window</span>
              <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
                {WINDOWS.map((w) => (
                  <button
                    key={w.hours}
                    type="button"
                    onClick={() => setHours(w.hours)}
                    className={`rounded-md px-3 py-1 ${
                      hours === w.hours ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            {statsQ.isLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : statsQ.data ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Stat
                    label="Total executions"
                    value={(statsQ.data.total_executions ?? 0).toLocaleString()}
                  />
                  <Stat
                    label="Success rate"
                    value={
                      statsQ.data.success_pct == null
                        ? '—'
                        : `${Number(statsQ.data.success_pct).toFixed(1)}%`
                    }
                  />
                  <Stat
                    label="p50 duration"
                    value={formatDuration(statsQ.data.success_duration_ms?.p50)}
                  />
                  <Stat
                    label="p95 duration"
                    value={formatDuration(statsQ.data.success_duration_ms?.p95)}
                  />
                </div>

                {statsQ.data.sampled_note && (
                  <p className="text-xs text-amber-700">{statsQ.data.sampled_note}</p>
                )}

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-800">By status</h3>
                    <ul className="mt-3 space-y-2 text-sm">
                      {Object.entries(statsQ.data.by_status ?? {}).map(([k, v]) => (
                        <li key={k} className="flex justify-between">
                          <span className="capitalize text-gray-600">{k}</span>
                          <span className="font-medium text-gray-900">{v.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Top failing nodes
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        sample {statsQ.data.errors_sample_size}
                      </span>
                    </h3>
                    {failingNodes.length === 0 ? (
                      <p className="mt-3 text-sm text-gray-400">No errors in this window.</p>
                    ) : (
                      <ul className="mt-3 space-y-2 text-sm">
                        {failingNodes.map(([node, count]) => (
                          <li key={node} className="flex justify-between gap-3">
                            <span className="truncate text-gray-700">{node}</span>
                            <span className="shrink-0 font-medium text-red-600">{count}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {(workflow?.webhook_paths?.length ?? 0) > 0 && (
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-800">Webhook paths</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {workflow!.webhook_paths.map((p) => (
                        <code
                          key={p}
                          className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700"
                        >
                          {p}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : statsQ.isError ? (
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                {getApiErrorMessage(statsQ.error, 'Failed to load stats')}
              </div>
            ) : null}
          </div>
        )}

        {tab === 'executions' && (
          <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
            <div className="space-y-3">
              <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm w-fit">
                {(
                  [
                    ['', 'All'],
                    ['success', 'Success'],
                    ['error', 'Error'],
                    ['waiting', 'Waiting'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value || 'all'}
                    type="button"
                    onClick={() => {
                      setExecStatus(value);
                      setSelectedExecId(null);
                    }}
                    className={`rounded-md px-3 py-1.5 ${
                      execStatus === value
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                {execsQ.isLoading ? (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (execsQ.data?.executions ?? []).length === 0 ? (
                  <p className="px-4 py-10 text-center text-sm text-gray-400">No executions.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                        <th className="px-4 py-2 font-medium">ID</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Duration</th>
                        <th className="px-4 py-2 font-medium">Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {execsQ.data!.executions.map((ex) => (
                        <tr
                          key={ex.id}
                          onClick={() => setSelectedExecId(ex.id)}
                          className={`cursor-pointer border-b border-gray-50 last:border-0 hover:bg-gray-50 ${
                            selectedExecId === ex.id ? 'bg-slate-50' : ''
                          }`}
                        >
                          <td className="px-4 py-2 font-mono text-xs">{ex.id}</td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${statusBadgeClass(ex.status)}`}
                            >
                              {ex.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {formatDuration(ex.duration_ms)}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {formatDateTime(ex.started_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800">Execution detail</h3>
              {!selectedExecId ? (
                <p className="mt-4 text-sm text-gray-400">Select an execution to inspect.</p>
              ) : execDetailQ.isLoading ? (
                <div className="mt-4 space-y-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : execDetailQ.data?.execution ? (
                <div className="mt-4 space-y-3 text-sm">
                  <Row label="ID" value={execDetailQ.data.execution.id} mono />
                  <Row label="Status" value={execDetailQ.data.execution.status} />
                  <Row
                    label="Duration"
                    value={formatDuration(execDetailQ.data.execution.duration_ms)}
                  />
                  <Row
                    label="Started"
                    value={formatDateTime(execDetailQ.data.execution.started_at)}
                  />
                  <Row
                    label="Stopped"
                    value={formatDateTime(execDetailQ.data.execution.stopped_at)}
                  />
                  {execDetailQ.data.execution.error_detail && (
                    <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-red-800">
                      <p className="font-medium">
                        {execDetailQ.data.execution.error_detail.failing_node ?? 'Error'}
                      </p>
                      <p className="mt-1 font-mono text-xs whitespace-pre-wrap">
                        {execDetailQ.data.execution.error_detail.error}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-4 text-sm text-red-600">
                  {getApiErrorMessage(execDetailQ.error, 'Failed to load execution')}
                </p>
              )}
            </div>
          </div>
        )}

        {tab === 'nodes' && (
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {detailQ.isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (workflow?.nodes ?? []).length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">No nodes.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Version</th>
                    <th className="px-4 py-3 font-medium">Retry on fail</th>
                    <th className="px-4 py-3 font-medium">Max tries</th>
                  </tr>
                </thead>
                <tbody>
                  {workflow!.nodes.map((n) => (
                    <tr key={n.name} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2 font-medium text-gray-800">{n.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-600">{n.type}</td>
                      <td className="px-4 py-2 text-gray-600">{n.type_version ?? '—'}</td>
                      <td className="px-4 py-2">
                        {n.retry_on_fail == null ? (
                          <span className="text-gray-400">—</span>
                        ) : n.retry_on_fail ? (
                          <span className="text-emerald-700">yes</span>
                        ) : (
                          <span className="text-amber-700">no</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{n.max_tries ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
  </div>
);

const Row = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className="flex justify-between gap-3">
    <span className="text-gray-500">{label}</span>
    <span className={`text-right text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

export default WorkflowDetailPage;
